import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ArrowLeft, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

const EMPTY = { title: "", slug: "", content: "", in_menu: false };

export default function PagesTab() {
  const [pages, setPages] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api("/pages").then(setPages).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api(`/admin/pages/${editing.id}`, { method: "PUT", body: editing });
        toast.success("Page updated");
      } else {
        await api("/admin/pages", { method: "POST", body: editing });
        toast.success("Page created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the page "${p.title}"?`)) return;
    try {
      await api(`/admin/pages/${p.id}`, { method: "DELETE" });
      toast.success("Page deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (editing) {
    const set = (k, v) => setEditing({ ...editing, [k]: v });
    return (
      <div data-testid="page-editor">
        <button onClick={() => setEditing(null)} data-testid="page-back-button" className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-ink/60 hover:text-gold font-semibold">
          <ArrowLeft size={14} /> All Pages
        </button>
        <h2 className="font-display text-3xl text-ink mt-4">{editing.id ? "Edit Page" : "New Page"}</h2>

        <div className="mt-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Title</label>
              <input data-testid="page-title-input" value={editing.title} onChange={(e) => set("title", e.target.value)}
                className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Slug (optional)</label>
              <input data-testid="page-slug-input" value={editing.slug} onChange={(e) => set("slug", e.target.value)}
                placeholder="about-us"
                className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Content</label>
            <p className="text-xs text-neutral-500 mb-2">Same format as articles: blank line between paragraphs, ## heading, &gt; quote, img: URL | caption.</p>
            <textarea data-testid="page-content-input" rows={12} value={editing.content} onChange={(e) => set("content", e.target.value)}
              className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white font-mono" />
          </div>
          <label className="flex items-center gap-3 text-sm text-ink/80">
            <input type="checkbox" data-testid="page-menu-toggle" checked={editing.in_menu} onChange={(e) => set("in_menu", e.target.checked)} className="w-4 h-4 accent-[#B89742]" />
            Show in site menu
          </label>
          <button onClick={save} data-testid="page-save-button" disabled={!editing.title}
            className="bg-ink text-cream px-8 py-3.5 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-gold transition-colors duration-300 disabled:opacity-40">
            {editing.id ? "Save Changes" : "Create Page"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pages-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-ink">Pages</h2>
          <p className="mt-2 text-sm text-neutral-500">Add standalone pages like About, Press or a Vendor Directory.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="page-new-button"
          className="flex items-center gap-2 bg-ink text-cream px-6 py-3 text-[10px] uppercase tracking-[0.25em] font-semibold hover:bg-gold transition-colors duration-300">
          <Plus size={14} /> New Page
        </button>
      </div>

      <div className="mt-8 divide-y divide-gold/15 border-y border-gold/15">
        {!pages ? (
          <p className="py-6 font-display italic text-gold">Loading</p>
        ) : pages.length === 0 ? (
          <p className="py-6 text-sm text-neutral-500">No custom pages yet.</p>
        ) : (
          pages.map((p) => (
            <div key={p.id} data-testid={`page-row-${p.slug}`} className="py-5 flex items-center gap-5">
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg text-ink truncate">{p.title}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50 mt-1">/page/{p.slug} · {p.in_menu ? "In menu" : "Hidden"}</p>
              </div>
              <a href={`/page/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`page-view-${p.slug}`} aria-label="View"
                className="w-10 h-10 border border-ink/15 flex items-center justify-center text-ink/60 hover:border-gold hover:text-gold transition-colors duration-300">
                <ExternalLink size={15} />
              </a>
              <button onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`page-edit-${p.slug}`} aria-label="Edit"
                className="w-10 h-10 border border-ink/15 flex items-center justify-center text-ink/60 hover:border-gold hover:text-gold transition-colors duration-300">
                <Pencil size={15} />
              </button>
              <button onClick={() => remove(p)} data-testid={`page-delete-${p.slug}`} aria-label="Delete"
                className="w-10 h-10 border border-ink/15 flex items-center justify-center text-ink/60 hover:border-red-400 hover:text-red-500 transition-colors duration-300">
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
